import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, User } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [classesDropdownOpen, setClassesDropdownOpen] = useState(false);
  const [membershipDropdownOpen, setMembershipDropdownOpen] = useState(false);
  const [contactDropdownOpen, setContactDropdownOpen] = useState(false);
  const aboutTimeoutRef = useRef(null);
  const classesTimeoutRef = useRef(null);
  const membershipTimeoutRef = useRef(null);
  const contactTimeoutRef = useRef(null);
  const location = useLocation();
  const { isAuthenticated, isAdmin, user } = useAuth();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Calendar', path: '/calendar' },
    { name: 'Workshops', path: '/workshops' },
    { name: 'Support', path: '/support' },
  ];

  const contactSubmenu = [
    { name: 'Contact Us', path: '/contact' },
    { name: 'Become a Model', path: '/contact/model' },
  ];

  const aboutSubmenu = [
    { name: 'About Us', path: '/about' },
    { name: 'Our History', path: '/about/history' },
    { name: 'Pompeo Coppini', path: '/about/pompeo-coppini' },
    { name: 'Waldine Tauch', path: '/about/waldine-tauch' },
    { name: 'The First Fiesta Medal', path: '/about/fiesta-medal' }
  ];

  const classesSubmenu = [
    { name: 'Adult Classes', path: '/classes/adult' },
    { name: 'Kids Classes', path: '/classes/kids' },
    { name: 'Open Sessions', path: '/classes/open-sessions' },
    { name: 'Our Instructors', path: '/classes/instructors' }
  ];

  const membershipSubmenu = [
    { name: 'Join CAFA', path: '/membership' },
    { name: 'Member Login', path: '/members/login', hideWhenAuth: true },
    { name: 'Member Directory', path: '/members/directory', requireAuth: true },
    { name: 'Submissions', path: '/members/submissions', requireAuth: true },
    { name: 'Gallery Applications', path: '/members/gallery', requireAuth: true },
    { name: 'My Profile', path: '/members/profile', requireAuth: true },
    { name: 'My History', path: '/members/history', requireAuth: true },
    { name: 'Admin: Members', path: '/admin/members', requireAdmin: true },
    { name: 'Admin: Registrations', path: '/admin/registrations', requireAdmin: true },
    { name: 'Gallery Staff', path: '/admin/gallery', requireGalleryStaff: true }
  ];

  const isActive = (path) => location.pathname === path;
  const isAboutActive = () => location.pathname.startsWith('/about');
  const isClassesActive = () => location.pathname.startsWith('/classes');
  const isMembershipActive = () => location.pathname.startsWith('/members') || location.pathname === '/membership';

  const filteredMembershipSubmenu = membershipSubmenu.filter(item => {
    if (item.hideWhenAuth && isAuthenticated) return false;
    if (item.requireAuth && !isAuthenticated) return false;
    if (item.requireAdmin && !isAdmin) return false;
    if (item.requireGalleryStaff && !isAdmin && !(user?.gallery_roles || []).length) return false;
    return true;
  });

  // Dropdown handlers with delay to prevent accidental closing
  const handleAboutMouseEnter = () => {
    if (aboutTimeoutRef.current) {
      clearTimeout(aboutTimeoutRef.current);
    }
    setAboutDropdownOpen(true);
  };

  const handleAboutMouseLeave = () => {
    aboutTimeoutRef.current = setTimeout(() => {
      setAboutDropdownOpen(false);
    }, 150);
  };

  const handleClassesMouseEnter = () => {
    if (classesTimeoutRef.current) {
      clearTimeout(classesTimeoutRef.current);
    }
    setClassesDropdownOpen(true);
  };

  const handleClassesMouseLeave = () => {
    classesTimeoutRef.current = setTimeout(() => {
      setClassesDropdownOpen(false);
    }, 150);
  };

  const handleContactMouseEnter = () => {
    if (contactTimeoutRef.current) clearTimeout(contactTimeoutRef.current);
    setContactDropdownOpen(true);
  };
  const handleContactMouseLeave = () => {
    contactTimeoutRef.current = setTimeout(() => setContactDropdownOpen(false), 150);
  };
  const isContactActive = () => location.pathname.startsWith('/contact') || location.pathname === '/become-a-model';

  const handleMembershipMouseEnter = () => {
    if (membershipTimeoutRef.current) {
      clearTimeout(membershipTimeoutRef.current);
    }
    setMembershipDropdownOpen(true);
  };

  const handleMembershipMouseLeave = () => {
    membershipTimeoutRef.current = setTimeout(() => {
      setMembershipDropdownOpen(false);
    }, 150);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            {/* Desktop logo - small circular icon */}
            <img 
              src="https://customer-assets.emergentagent.com/job_fine-art-academy/artifacts/xr18kvxc_CAFA_LOGO_trans.png" 
              alt="The Coppini Academy of Fine Art" 
              className="hidden lg:block h-10 lg:h-12 w-auto"
            />
            {/* Mobile/Tablet logo - full color banner logo (larger) */}
            <img 
              src="https://customer-assets.emergentagent.com/job_artsite-debug/artifacts/d797uds2_logobanner_web.png" 
              alt="The Coppini Academy of Fine Art" 
              className="lg:hidden h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation - only show on large screens */}
          <nav className="hidden lg:flex items-center space-x-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-md ${
                isActive('/')
                  ? 'text-cafa-red bg-cafa-red-50'
                  : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
              }`}
            >
              Home
            </Link>
            
            {/* About Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleAboutMouseEnter}
              onMouseLeave={handleAboutMouseLeave}
            >
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-md flex items-center gap-1 ${
                  isAboutActive()
                    ? 'text-cafa-red bg-cafa-red-50'
                    : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                }`}
                data-testid="about-dropdown-trigger"
              >
                About
                <ChevronDown size={14} className={`transition-transform ${aboutDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {aboutDropdownOpen && (
                <div className="absolute top-full left-0 pt-1 w-48 z-50">
                  <div className="bg-white border border-stone-200 rounded-lg shadow-lg py-2">
                    {aboutSubmenu.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block px-4 py-2 text-sm transition-colors ${
                          isActive(item.path)
                            ? 'text-cafa-red bg-cafa-red-50'
                            : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                        }`}
                        data-testid={`about-dropdown-${item.name.toLowerCase().replace(' ', '-')}`}
                        onClick={() => setAboutDropdownOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Classes Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleClassesMouseEnter}
              onMouseLeave={handleClassesMouseLeave}
            >
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-md flex items-center gap-1 ${
                  isClassesActive()
                    ? 'text-cafa-red bg-cafa-red-50'
                    : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                }`}
                data-testid="classes-dropdown-trigger"
              >
                Classes
                <ChevronDown size={14} className={`transition-transform ${classesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {classesDropdownOpen && (
                <div className="absolute top-full left-0 pt-1 w-48 z-50">
                  <div className="bg-white border border-stone-200 rounded-lg shadow-lg py-2">
                    {classesSubmenu.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block px-4 py-2 text-sm transition-colors ${
                          isActive(item.path)
                            ? 'text-cafa-red bg-cafa-red-50'
                            : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                        }`}
                        data-testid={`classes-dropdown-${item.name.toLowerCase().replace(' ', '-')}`}
                        onClick={() => setClassesDropdownOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/workshops"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-md ${
                isActive('/workshops')
                  ? 'text-cafa-red bg-cafa-red-50'
                  : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
              }`}
            >
              Workshops
            </Link>

            <Link
              to="/di-barbieri-gallery"
              data-testid="nav-di-barbieri-gallery-link"
              className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                isActive('/di-barbieri-gallery')
                  ? 'text-cafa-red bg-cafa-red-50'
                  : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
              }`}
            >
              Di Barbieri Gallery
            </Link>

            <Link
              to="/calendar"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-md ${
                isActive('/calendar')
                  ? 'text-cafa-red bg-cafa-red-50'
                  : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
              }`}
            >
              Calendar
            </Link>


            {/* Membership Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleMembershipMouseEnter}
              onMouseLeave={handleMembershipMouseLeave}
            >
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-md flex items-center gap-1 ${
                  isMembershipActive()
                    ? 'text-cafa-red bg-cafa-red-50'
                    : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                }`}
                data-testid="membership-dropdown-trigger"
              >
                Membership
                <ChevronDown size={14} className={`transition-transform ${membershipDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {membershipDropdownOpen && (
                <div className="absolute top-full left-0 pt-1 w-48 z-50">
                  <div className="bg-white border border-stone-200 rounded-lg shadow-lg py-2">
                    {filteredMembershipSubmenu.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block px-4 py-2 text-sm transition-colors ${
                          isActive(item.path)
                            ? 'text-cafa-red bg-cafa-red-50'
                            : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                        }`}
                        onClick={() => setMembershipDropdownOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/support"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-md ${
                isActive('/support')
                  ? 'text-cafa-red bg-cafa-red-50'
                  : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
              }`}
            >
              Support
            </Link>

            {/* Contact Dropdown */}
            <div
              className="relative"
              onMouseEnter={handleContactMouseEnter}
              onMouseLeave={handleContactMouseLeave}
            >
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-md flex items-center gap-1 ${
                  isContactActive()
                    ? 'text-cafa-red bg-cafa-red-50'
                    : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                }`}
                data-testid="contact-dropdown-trigger"
              >
                Contact
                <ChevronDown size={14} className={`transition-transform ${contactDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {contactDropdownOpen && (
                <div className="absolute top-full right-0 pt-1 w-56 z-50">
                  <div className="bg-white border border-stone-200 rounded-lg shadow-lg py-2">
                    {contactSubmenu.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block px-4 py-2 text-sm transition-colors ${
                          location.pathname === item.path
                            ? 'text-cafa-red bg-cafa-red-50'
                            : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                        }`}
                        data-testid={`contact-dropdown-${item.name.toLowerCase().replace(/ /g, '-')}`}
                        onClick={() => setContactDropdownOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            {isAuthenticated ? (
              <Link 
                to="/members/profile"
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-600 hover:text-cafa-red transition-colors"
              >
                <User size={18} />
                {user?.first_name}
              </Link>
            ) : (
              <Button asChild className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                <Link to="/membership">Join Now</Link>
              </Button>
            )}
          </div>

          {/* Mobile/Tablet Menu Button - show on screens smaller than lg */}
          <button
            className="lg:hidden p-2 text-stone-600 hover:text-stone-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-navigation-toggle"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile/Tablet Navigation */}
        {mobileMenuOpen && (
          <nav
            className="lg:hidden py-4 border-t border-stone-200 overflow-y-auto"
            style={{ maxHeight: 'calc(100vh - 4rem)' }}
            data-testid="mobile-nav-menu"
          >
            <Link
              to="/"
              className={`block px-4 py-3 text-sm font-medium transition-colors ${
                isActive('/')
                  ? 'text-cafa-red bg-cafa-red-50'
                  : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            
            {/* Mobile About Submenu */}
            <div className="border-l-2 border-cafa-blue-200 ml-4">
              <p className="px-4 py-2 text-sm font-semibold text-stone-700">About</p>
              {aboutSubmenu.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-4 py-2 text-sm transition-colors ${
                    isActive(item.path)
                      ? 'text-cafa-red bg-cafa-red-50'
                      : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
            
            {/* Mobile Classes Submenu */}
            <div className="border-l-2 border-cafa-blue-200 ml-4">
              <p className="px-4 py-2 text-sm font-semibold text-stone-700">Classes</p>
              {classesSubmenu.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-4 py-2 text-sm transition-colors ${
                    isActive(item.path)
                      ? 'text-cafa-red bg-cafa-red-50'
                      : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            <Link
              to="/workshops"
              className={`block px-4 py-3 text-sm font-medium transition-colors ${
                isActive('/workshops')
                  ? 'text-cafa-red bg-cafa-red-50'
                  : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Workshops
            </Link>

            <Link
              to="/di-barbieri-gallery"
              data-testid="mobile-nav-di-barbieri-gallery-link"
              className={`block px-4 py-3 text-sm font-medium transition-colors ${
                isActive('/di-barbieri-gallery')
                  ? 'text-cafa-red bg-cafa-red-50'
                  : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Di Barbieri Gallery
            </Link>

            <Link
              to="/calendar"
              className={`block px-4 py-3 text-sm font-medium transition-colors ${
                isActive('/calendar')
                  ? 'text-cafa-red bg-cafa-red-50'
                  : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Calendar
            </Link>


            {/* Mobile Membership Submenu */}
            <div className="border-l-2 border-cafa-blue-200 ml-4">
              <p className="px-4 py-2 text-sm font-semibold text-stone-700">Membership</p>
              {filteredMembershipSubmenu.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-4 py-2 text-sm transition-colors ${
                    isActive(item.path)
                      ? 'text-cafa-red bg-cafa-red-50'
                      : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            <Link
              to="/support"
              className={`block px-4 py-3 text-sm font-medium transition-colors ${
                isActive('/support')
                  ? 'text-cafa-red bg-cafa-red-50'
                  : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Support
            </Link>

            <div className="pt-2 mt-2 border-t border-stone-100">
              <div className="px-4 py-2 text-xs font-semibold text-stone-400 uppercase tracking-wider">Contact</div>
              {contactSubmenu.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-4 py-3 text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'text-cafa-red bg-cafa-red-50'
                      : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-contact-${item.name.toLowerCase().replace(/ /g, '-')}`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            <div className="px-4 pt-4">
              {isAuthenticated ? (
                <Link 
                  to="/members/profile"
                  className="flex items-center gap-2 text-cafa-red"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User size={18} />
                  My Account
                </Link>
              ) : (
                <Button asChild className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white">
                  <Link to="/membership" onClick={() => setMobileMenuOpen(false)}>
                    Join Now
                  </Link>
                </Button>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
